import { useState, useMemo } from "react";
import { useDispatch } from 'react-redux';
import { useTypedSelector } from "../../../store/reducers/use-typed-selector";
import { updateChannel } from "../../../store/actions/authentication";
import { Channel } from "../../../types/working-model";
import ReactSelect from "react-select";
import ModalComponent from "../../general-ui/modal/modal";
import { reactSelectStyles } from "../../general-ui/react-select/react-select-styles";
import TextInput from "../../general-ui/text-input/text";
// import { validateUrl } from "../../../utils/utils";
import "./edit-channel.scss";

interface EditChannelModalProps {
	isOpen: boolean;
	onClose: () => void;
}

// This modal was designed with an editable url field that is not being included at this time
// code for the url is commented out below
export default function EditChannelModal({ isOpen, onClose }: EditChannelModalProps): JSX.Element {
	const { token, user, channels } = useTypedSelector((state) => state.AuthReducer);
	// can user get to this page directly without starting at events page?
	// if so, need to dispatch for admins
	const { admins } = useTypedSelector(state => state.ChannelAdminReducer);

	const dispatch = useDispatch();

	const userPermission = useMemo(() => {
		return user?.channels[user?.active_channel].some(permission => permission === "owner" || permission === "admin");
	}, [user]);

	const currentChannel: Channel | undefined = useMemo(() => {
		return channels.find((channel) => channel.channel === user?.active_channel);
	}, [user, channels]);

	const [channelEdits, setChannelEdits] = useState({
		name: currentChannel?.name,
		primary_contact: currentChannel?.primary_contact
	});
	// const [editErrors, setEditErrors] = useState({ name: false, url: false });
	const [editErrors, setEditErrors] = useState({ name: false });

	const getEmail = (id: number | null | undefined): string => {
		const email = id && admins.find(admin => admin.id === id)?.email;
		return email || "";
	};

	const validateEdits = (): boolean => {
		let valid = true;
		// const errors = { name: false, url: false };
		const errors = { name: false };
		if (!channelEdits.name) {
			errors.name = true;
			valid = false;
		}
		// if (!channelEdits.url || !validateUrl(channelEdits.url)) {
		// 	errors.url = true;
		// 	valid = false;
		// }
		// If url is used, also need to verify if url is available
		setEditErrors(errors);
		return valid;
	};

	const handleSave = (): void => {
		if (validateEdits()) {
			const channelUpdate = {
				...currentChannel,
				...channelEdits
			};
			dispatch(updateChannel(token!, channelUpdate as Channel));
			onClose();
		}
	};

	return (
		<ModalComponent
			title="Edit channel information"
			open={isOpen}
			closeable={false}
			cancellable={true}
			onRequestClose={onClose}
			footer={
				<>
					<button onClick={onClose} style={{ marginRight: 16 }}>
						Cancel
					</button>
					<button onClick={handleSave} className="primary" disabled={!userPermission}>
						Save
					</button>
				</>
			}
		>
			<div className="channel-modal">
				{userPermission ? (<>
					<TextInput
						label={"Company name*"}
						id={"companyName"}
						onChange={(event) => setChannelEdits({ ...channelEdits, name: event.target.value })}
						defaultValue={channelEdits.name}
					/>
					{editErrors.name && <div className="entry-error">"Company name is required"</div>}
					<div><label className="select-label">Primary contact</label></div>
					<ReactSelect
						styles={reactSelectStyles({
							menu: {
								right: 0,
								top: 0,
							},
							menuList: {
								fontSize: "14px"
							},
							control: {
								borderRadius: 100,
								marginTop: "5px",
								fontSize: "14px"
							},
						})}
						options={admins.map((admin) => { return { label: admin.email, value: admin.id.toString() }; })}
						defaultValue={{ label: getEmail(channelEdits.primary_contact), value: channelEdits.primary_contact?.toString() || "" }}
						onChange={value => setChannelEdits({ ...channelEdits, primary_contact: value && parseInt(value.value) })}
						isClearable={false}
						menuPortalTarget={document.body}
					/>
					{/* Not including URL field at this time due to huge implications to past events if url is changed */}
					{/* <TextInput
						label={"URL used for events"}
						id={"url"}
						onChange={(event) => setChannelEdits({ ...channelEdits, url: event.target.value })}
						defaultValue={channelEdits.url}
					/>
					{editErrors.url && <div className="entry-error">"Invalid url"</div>} */}
				</>) : (
					<p>You must be an Owner or Admin to edit this channel.</p>
				)}
			</div>
		</ModalComponent>
	);
}